import { getImage } from '../data/images';

type Props = {
  id: string;
  className?: string;
  /** Aspect ratio as a CSS aspect-ratio string, e.g. '16 / 10', '3 / 4'. */
  aspect?: string;
  /** Hide the placeholder caption even when no image src is set. */
  silent?: boolean;
  /** Object-fit positioning, e.g. 'center', 'top', 'bottom'. */
  position?: string;
  /** Loading attribute for the <img>. Default 'lazy'. */
  loading?: 'lazy' | 'eager';
};

/**
 * ImageSlot renders an image from the central registry by id.
 * If the registry has no `src` set, it renders a styled placeholder
 * that shows the slot id, description, and recommended dimensions
 * so designers can see at a glance where art is missing.
 */
export default function ImageSlot({
  id,
  className = '',
  aspect,
  silent = false,
  position = 'center',
  loading = 'lazy',
}: Props) {
  const ref = getImage(id);
  const style: React.CSSProperties = aspect ? { aspectRatio: aspect } : {};

  return (
    <div className={`image-slot ${className}`} style={style}>
      {ref.src ? (
        <img
          src={ref.src}
          alt={ref.alt}
          loading={loading}
          style={{ objectPosition: position }}
        />
      ) : (
        !silent && (
          <div className="image-slot-meta">
            <div className="slot-id">{id}</div>
            <div className="slot-desc">{ref.description}</div>
            <div className="slot-dim">{ref.dimensions}</div>
          </div>
        )
      )}
    </div>
  );
}
